/**
 * Utility for announcing messages to screen readers using ARIA live regions.
 * This allows dynamic content updates to be announced to users of assistive technologies.
 */

let announcementContainer = null;

/**
 * Creates or returns the announcement container element.
 * The container is visually hidden but accessible to screen readers.
 * @returns {HTMLElement} The announcement container
 */
function getAnnouncementContainer() {
    if (announcementContainer && document.body.contains(announcementContainer)) {
        return announcementContainer;
    }

    announcementContainer = document.createElement('div');
    announcementContainer.id = 'sr-announcements';
    announcementContainer.setAttribute('aria-live', 'polite');
    announcementContainer.setAttribute('aria-atomic', 'true');
    announcementContainer.setAttribute('role', 'status');

    // Visually hidden but accessible to screen readers
    Object.assign(announcementContainer.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: '0',
    });

    document.body.appendChild(announcementContainer);
    return announcementContainer;
}

/**
 * Announces a message to screen readers.
 *
 * @param {string} message - The message to announce
 * @param {Object} options - Options for the announcement
 * @param {('polite'|'assertive')} options.priority - The urgency of the announcement
 *   - 'polite': Waits for the user to finish their current task (default)
 *   - 'assertive': Interrupts the user immediately (use sparingly)
 * @param {number} options.clearDelay - Time in ms before clearing the message (default: 1000)
 *
 * @example
 * // Polite announcement (default)
 * announceToScreenReader('Text selected. Options available.');
 *
 * // Assertive announcement (interrupts)
 * announceToScreenReader('Error: Connection lost', { priority: 'assertive' });
 */
export function announceToScreenReader(message, options = {}) {
    const { priority = 'polite', clearDelay = 1000 } = options;

    const container = getAnnouncementContainer();
    container.setAttribute('aria-live', priority);

    // Clear previous content first to ensure the new message is announced
    container.textContent = '';

    // Use requestAnimationFrame to ensure the DOM update is processed
    requestAnimationFrame(() => {
        container.textContent = message;

        // Clear the message after a delay to allow for re-announcements
        setTimeout(() => {
            if (container.textContent === message) {
                container.textContent = '';
            }
        }, clearDelay);
    });
}

/**
 * Announces text selection to screen readers with contextual information.
 *
 * @param {Object} params - Parameters for the selection announcement
 * @param {string} params.selectedText - The selected text (will be truncated if too long)
 * @param {number} params.charCount - Number of characters selected
 * @param {string} params.lang - Language code for localized messages
 * @param {Object} params.localizations - Localization strings object
 */
export function announceTextSelection({ selectedText, charCount, lang, localizations }) {
    const maxPreviewLength = 50;
    const truncatedText =
        selectedText.length > maxPreviewLength ? `${selectedText.substring(0, maxPreviewLength)}...` : selectedText;

    const message = localizations?.[lang]?.accessibility?.textSelected
        ? localizations[lang].accessibility.textSelected
              .replace('{count}', charCount)
              .replace('{preview}', truncatedText)
        : `${charCount} characters selected. Selection tools available.`;

    announceToScreenReader(message);
}

/**
 * Announces page changes to screen readers.
 *
 * @param {Object} params - Parameters for the page announcement
 * @param {number} params.currentPage - Current page number
 * @param {number} params.totalPages - Total number of pages
 * @param {string} params.lang - Language code for localized messages
 * @param {Object} params.localizations - Localization strings object
 */
export function announcePageChange({ currentPage, totalPages, lang, localizations }) {
    const message = localizations?.[lang]?.accessibility?.pageChanged
        ? localizations[lang].accessibility.pageChanged.replace('{current}', currentPage).replace('{total}', totalPages)
        : `Page ${currentPage} of ${totalPages}`;

    announceToScreenReader(message);
}

export default {
    announceToScreenReader,
    announceTextSelection,
    announcePageChange,
};
